import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { INFO_PAGES, TEST_MODE_SENTENCE } from "@/data/info";
import { INFO_SEO } from "@/data/categorySeo";
import { Slogan, SLOGAN } from "@/components/shop/Slogan";
import { api } from "@/lib/api";
import { useSeo, breadcrumbLd, clip } from "@/lib/seo";
import { Breadcrumbs } from "@/components/shop/Breadcrumbs";
import NotFound from "@/pages/NotFound";

const EMAIL = "hello@shopabee.co.uk";
const Text = ({ children }) => children.split(/(\[[^\]]+\]|hello@shopabee\.co\.uk)/).map((part, i) =>
  part === EMAIL ? <a key={i} href={`mailto:${EMAIL}`} className="font-semibold underline text-[#8A5E00]" data-testid="info-email-link">{EMAIL}</a>
  : part.startsWith("[") ? <mark key={i} className="bg-[#FFE5D1] text-[#8A3B00] font-semibold rounded px-1" data-testid="info-tbc">{part}</mark> : part);

export default function InfoPage() {
  const { slug } = useParams();
  const page = INFO_PAGES[slug];
  const path = `/info/${slug}`;
  const crumbs = page ? [{ name: "Home", path: "/" }, { name: page.title, path }] : [];
  const firstText = page && [page.intro === SLOGAN ? "" : page.intro, ...page.sections.flatMap((s) => [...(s.p || []), ...(s.list || [])])].filter(Boolean).join(" ");
  useSeo(page ? { title: INFO_SEO[slug]?.title || `${page.title} | ShopaBee UK`, description: INFO_SEO[slug]?.description || clip(firstText), path,
    jsonLd: { "@context": "https://schema.org", "@graph": [breadcrumbLd(crumbs)] } } : { title: "Page not found", noindex: true });
  const [testMode, setTestMode] = useState(false); // hidden until the server confirms test mode
  useEffect(() => {
    api.get("/delivery").then((r) => setTestMode(r.data.test_mode === true)).catch(() => setTestMode(false));
  }, []);
  const visible = (t) => t !== TEST_MODE_SENTENCE || testMode;
  if (!page) return <NotFound testId="info-not-found" />;
  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid={`info-page-${slug}`}>
      <Breadcrumbs items={crumbs} />
      <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="info-title">{page.title}</h1>
      <p className="mt-3 text-base md:text-lg text-[#202A35]/70">{page.intro === SLOGAN ? <Slogan /> : page.intro}</p>
      <div className="mt-8 space-y-7 text-sm sm:text-base leading-relaxed text-[#202A35]/85">
        {page.sections.map((s, i) => (
          <section key={i}>
            {s.h && <h2 className="font-display text-base md:text-lg font-semibold text-[#202A35] mb-2">{s.h}</h2>}
            {s.p?.filter(visible).map((t, j) => <p key={j} className="mb-2" data-testid={t === TEST_MODE_SENTENCE ? "terms-test-mode-sentence" : undefined}><Text>{t}</Text></p>)}
            {s.list && <ul className="list-disc pl-5 space-y-1.5">{s.list.map((t, j) => <li key={j}><Text>{t}</Text></li>)}</ul>}
          </section>
        ))}
      </div>
    </article>
  );
}
